/**
 * ScoreBirdGameCast — Live scoreboard display powered by ScoreBird NestCloud.
 * ESPN-Gamecast-style header bar + live football field view.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 3000;

/* ═══════════════════════════════════════
   HOOK — poll ScoreBird live data
   ═══════════════════════════════════════ */
export const useScoreBirdData = (scorebirdLink) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!!scorebirdLink);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!scorebirdLink) return;
    try {
      const res = await axios.get(`${API}/scorebird/live`, {
        params: { link: scorebirdLink },
      });
      if (res.data?.success && res.data.game) {
        setData(res.data.game);
        setError(null);
      }
    } catch (e) {
      console.error("ScoreBird fetch error:", e);
      setError("Unable to load scoreboard data");
    } finally {
      setLoading(false);
    }
  }, [scorebirdLink]);

  useEffect(() => {
    if (!scorebirdLink) { setData(null); setLoading(false); return; }
    setLoading(true);
    fetchData();
    intervalRef.current = setInterval(fetchData, POLL_MS);
    return () => clearInterval(intervalRef.current);
  }, [scorebirdLink, fetchData]);

  return { data, error, loading };
};


/* ═══════════════════════════════════════
   TIMEOUT SQUARES — filled = remaining
   ═══════════════════════════════════════ */
const TimeoutSquares = ({ remaining, total = 3, teamColor }) => {
  const count = parseInt(remaining) || 0;
  return (
    <div className="flex gap-0.5">
      {[...Array(total)].map((_, i) => (
        <div
          key={i}
          className="w-2.5 h-2.5 rounded-sm transition-colors duration-300"
          style={{ backgroundColor: i < count ? (teamColor || "#eab308") : "#3f3f46" }}
        />
      ))}
    </div>
  );
};


/* ═══════════════════════════════════════
   POSSESSION INDICATOR
   ═══════════════════════════════════════ */
const PossessionDot = ({ active }) =>
  active ? <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse inline-block" /> : null;


/* ═══════════════════════════════════════
   SCORE FLASH — sport-aware animations
   Every score: team-colored pulse on score number
   3PT / TD: overlay banner FIRST → hold old score → pulse reveals new score
   ═══════════════════════════════════════ */
const useScoreFlash = (score, teamColor, sport) => {
  const prevRef = useRef(null);
  const [event, setEvent] = useState(null);      // overlay phase (banner)
  const [pulse, setPulse] = useState(false);      // score pulse phase
  const [heldScore, setHeldScore] = useState(null); // old score held during overlay
  const timersRef = useRef([]);

  useEffect(() => {
    const curr = parseInt(score) || 0;
    const prev = prevRef.current;
    if (prev !== null && curr !== prev && curr > prev) {
      const diff = curr - prev;

      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];

      let type;
      if (sport === "basketball") {
        if (diff >= 3) type = "three";
        else if (diff === 2) type = "bucket";
        else type = "freethrow";
      } else {
        if (diff >= 6) type = "touchdown";
        else if (diff === 3) type = "fieldgoal";
        else if (diff === 2) type = "safety";
        else type = "pat";
      }

      const hasOverlay = type === "three" || type === "touchdown";

      if (hasOverlay) {
        // Hold old score during overlay
        setHeldScore(String(prev));
        setEvent({ type, color: teamColor });
        setPulse(false);

        const overlayMs = 3200;
        // After overlay: release score + start pulse
        const t1 = setTimeout(() => {
          setHeldScore(null);
          setEvent(null);
          setPulse(true);
        }, overlayMs);
        const t2 = setTimeout(() => setPulse(false), overlayMs + 1600);
        timersRef.current = [t1, t2];
      } else {
        setHeldScore(null);
        setEvent(null);
        setPulse(true);
        const t = setTimeout(() => setPulse(false), 1600);
        timersRef.current = [t];
      }
    }
    prevRef.current = curr;
  }, [score, teamColor, sport]);

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const cls = pulse ? "sb-score-pulse" : "";
  const displayScore = heldScore !== null ? heldScore : score;
  return { cls, event, displayScore };
};


/* ═══════════════════════════════════════
   SCORING OVERLAY — full-width team-colored banner
   ═══════════════════════════════════════ */
const SCORING_LABELS = {
  touchdown: "TOUCHDOWN",
  fieldgoal: "FIELD GOAL",
  safety: "SAFETY",
  three: "THREE POINTER",
  bucket: "BUCKET",
};

const ScoringOverlay = ({ event, teamAbbr }) => {
  if (!event || event.type === "pat" || event.type === "freethrow") return null;

  const label = SCORING_LABELS[event.type] || "";
  const isBig = event.type === "touchdown" || event.type === "three";

  return (
    <div
      className={`sb-scoring-overlay ${isBig ? "sb-scoring-td" : "sb-scoring-fg"}`}
      style={{ "--sb-team-color": event.color }}
      data-testid="scorebird-scoring-overlay"
    >
      <div className="sb-scoring-content">
        <span className="sb-scoring-label">{label}</span>
        <span className="sb-scoring-abbr">{teamAbbr}</span>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════
   HEADER BAR — sport router
   ═══════════════════════════════════════ */
const ScoreBirdHeader = ({ game, homeTeam, awayTeam }) => {
  if (game.sport === "basketball") {
    return <BasketballHeader game={game} homeTeam={homeTeam} awayTeam={awayTeam} />;
  }
  return <FootballHeader game={game} homeTeam={homeTeam} awayTeam={awayTeam} />;
};


/* ═══════════════════════════════════════
   BASKETBALL HEADER
   ═══════════════════════════════════════ */

/* Bonus badge: shows BONUS or DBL BNS */
const BonusBadge = ({ bonus, dblBonus }) => {
  const b = parseInt(bonus) || 0;
  const db = parseInt(dblBonus) || 0;
  if (db) return <span className="text-[8px] sm:text-[9px] font-black text-red-400 uppercase leading-none">DBL BNS</span>;
  if (b) return <span className="text-[8px] sm:text-[9px] font-black text-orange-400 uppercase leading-none">BONUS</span>;
  return null;
};

/* Possession arrow indicator — points to team that gets next alternating possession */
const PossArrow = ({ side, active, teamColor }) => {
  if (!active) return null;
  return (
    <span
      className="text-[10px] sm:text-xs font-black leading-none"
      style={{ color: teamColor }}
      title="Possession Arrow"
    >
      {side === "left" ? "\u25C0" : "\u25B6"}
    </span>
  );
};

/* Team stats column (fouls, TOL, poss arrow) shown between name and score */
const TeamStatsCol = ({ fouls, timeouts, teamColor, bonus, dblBonus, possActive, side }) => (
  <div className="flex flex-col items-center gap-0.5 px-1 sm:px-2 flex-shrink-0">
    {/* Foul count */}
    <div className="flex items-center gap-1">
      <span className="text-zinc-500 text-[8px] sm:text-[9px] uppercase tracking-wider font-bold">PF</span>
      <span className="text-white text-[10px] sm:text-xs font-black tabular-nums">{fouls || "0"}</span>
    </div>
    {/* TOL */}
    <div className="flex items-center gap-1">
      <span className="text-zinc-500 text-[8px] sm:text-[9px] uppercase tracking-wider font-bold">TOL</span>
      <span className="text-white text-[10px] sm:text-xs font-black tabular-nums">{timeouts || "0"}</span>
    </div>
    {/* Bonus + Poss Arrow row */}
    <div className="flex items-center gap-1 min-h-[12px]">
      <BonusBadge bonus={bonus} dblBonus={dblBonus} />
      <PossArrow side={side} active={possActive} teamColor={teamColor} />
    </div>
  </div>
);

const BasketballHeader = ({ game, homeTeam, awayTeam }) => {
  const homeColor = homeTeam?.color || "#333";
  const awayColor = awayTeam?.color || "#555";
  const { cls: homeFlash, event: homeEvent, displayScore: homeDisplay } = useScoreFlash(game.home_score, homeColor, "basketball");
  const { cls: awayFlash, event: awayEvent, displayScore: awayDisplay } = useScoreFlash(game.away_score, awayColor, "basketball");

  const homeName = homeTeam?.name || "Home";
  const awayName = awayTeam?.name || "Away";
  const homeAbbr = homeTeam?.abbreviation || homeTeam?.name?.substring(0, 3)?.toUpperCase() || "HME";
  const awayAbbr = awayTeam?.abbreviation || awayTeam?.name?.substring(0, 3)?.toUpperCase() || "AWY";
  const homeLogoUrl = homeTeam?.logo_url;
  const awayLogoUrl = awayTeam?.logo_url;
  const possession = game.possession; // "H" or "V" — alternating possession arrow

  const scoringEvent = homeEvent || awayEvent;
  const scoringAbr = homeEvent ? homeAbbr : awayEvent ? awayAbbr : "";

  const quarterLabel = parseInt(game.quarter) > 4 ? `OT${parseInt(game.quarter) - 4}` : `Q${game.quarter}`;

  return (
    <div className="bg-zinc-900 border border-white/10 relative overflow-hidden" data-testid="scorebird-header">
      <ScoringOverlay event={scoringEvent} teamAbbr={scoringAbr} />

      <div className="px-3 py-3 sm:px-5 sm:py-4 relative z-[1]">
        <div className="flex items-center justify-between gap-1 sm:gap-3">

          {/* Away team side */}
          <div className="flex items-center gap-1.5 sm:gap-2.5 flex-1 min-w-0">
            <TeamIcon url={awayLogoUrl} abbr={awayAbbr} color={awayColor} />
            <div className="min-w-0">
              <p className="text-white font-bold text-xs sm:text-sm truncate hidden sm:block">{awayName}</p>
              <p className="text-white font-bold text-xs sm:hidden">{awayAbbr}</p>
            </div>
            <TeamStatsCol
              fouls={game.away_fouls}
              timeouts={game.away_timeouts}
              teamColor={awayColor}
              bonus={game.away_bonus}
              dblBonus={game.away_dbl_bonus}
              possActive={possession === "A" || possession === "V"}
              side="left"
            />
            <span
              className={`text-white text-2xl sm:text-3xl md:text-4xl font-black ml-auto tabular-nums ${awayFlash}`}
              style={{ "--sb-team-color": awayColor }}
              data-testid="away-score"
            >
              {awayDisplay}
            </span>
          </div>

          {/* Center — Clock / Quarter / Shot Clock */}
          <div className="flex flex-col items-center gap-0.5 px-2 sm:px-5 flex-shrink-0">
            <span className="text-[9px] sm:text-[10px] font-bold uppercase px-2 py-0.5 bg-zinc-700 text-zinc-300 rounded">
              {quarterLabel}
            </span>
            <span className="text-white text-xl sm:text-2xl font-black tabular-nums leading-none">
              {game.time}
            </span>
            {game.play_clock && (
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-zinc-500 text-[8px] uppercase tracking-wider">Shot</span>
                <span className="text-yellow-500 text-xs sm:text-sm font-black tabular-nums">
                  {game.play_clock}
                </span>
              </div>
            )}
          </div>

          {/* Home team side */}
          <div className="flex items-center gap-1.5 sm:gap-2.5 flex-1 min-w-0 flex-row-reverse">
            <TeamIcon url={homeLogoUrl} abbr={homeAbbr} color={homeColor} />
            <div className="min-w-0 text-right">
              <p className="text-white font-bold text-xs sm:text-sm truncate hidden sm:block">{homeName}</p>
              <p className="text-white font-bold text-xs sm:hidden">{homeAbbr}</p>
            </div>
            <TeamStatsCol
              fouls={game.home_fouls}
              timeouts={game.home_timeouts}
              teamColor={homeColor}
              bonus={game.home_bonus}
              dblBonus={game.home_dbl_bonus}
              possActive={possession === "H"}
              side="right"
            />
            <span
              className={`text-white text-2xl sm:text-3xl md:text-4xl font-black mr-auto tabular-nums ${homeFlash}`}
              style={{ "--sb-team-color": homeColor }}
              data-testid="home-score"
            >
              {homeDisplay}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════
   FOOTBALL HEADER
   ═══════════════════════════════════════ */
const FootballHeader = ({ game, homeTeam, awayTeam }) => {
  const homeColor = homeTeam?.color || "#333";
  const awayColor = awayTeam?.color || "#555";
  const { cls: homeFlash, event: homeEvent, displayScore: homeDisplay } = useScoreFlash(game.home_score, homeColor, "football");
  const { cls: awayFlash, event: awayEvent, displayScore: awayDisplay } = useScoreFlash(game.away_score, awayColor, "football");

  const homeName = homeTeam?.name || "Home";
  const awayName = awayTeam?.name || "Away";
  const homeAbbr = homeTeam?.abbreviation || homeTeam?.name?.substring(0, 3)?.toUpperCase() || "HME";
  const awayAbbr = awayTeam?.abbreviation || awayTeam?.name?.substring(0, 3)?.toUpperCase() || "AWY";
  const homeLogoUrl = homeTeam?.logo_url;
  const awayLogoUrl = awayTeam?.logo_url;
  const possession = game.possession;

  const down = game.down;
  const toGo = game.to_go;
  const ballOn = game.ball_on;
  const downText = down && toGo ? `${down}${ordSuffix(down)} & ${toGo}` : "";
  const ballOnText = ballOn ? `on ${ballOn}` : "";

  const scoringEvent = homeEvent || awayEvent;
  const scoringAbbr = homeEvent ? homeAbbr : awayEvent ? awayAbbr : "";

  return (
    <div className="bg-zinc-900 border border-white/10 relative overflow-hidden" data-testid="scorebird-header">
      <ScoringOverlay event={scoringEvent} teamAbbr={scoringAbbr} />

      <div className="px-3 py-3 sm:px-5 sm:py-3.5 relative z-[1]">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          {/* Away team */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <PossessionDot active={possession === "A" || possession === "V"} />
            <TeamIcon url={awayLogoUrl} abbr={awayAbbr} color={awayColor} />
            <div className="hidden sm:block min-w-0">
              <p className="text-white font-bold text-sm truncate">{awayName}</p>
            </div>
            <div className="sm:hidden min-w-0">
              <p className="text-white font-bold text-xs">{awayAbbr}</p>
            </div>
            <TimeoutSquares remaining={game.away_timeouts} teamColor={awayColor} />
            <span
              className={`text-white text-xl sm:text-2xl md:text-3xl font-black ml-auto tabular-nums ${awayFlash}`}
              style={{ "--sb-team-color": awayColor }}
            >
              {awayDisplay}
            </span>
          </div>

          {/* Center — Clock / Quarter / Play Clock */}
          <div className="flex flex-col items-center gap-0.5 px-2 sm:px-4 flex-shrink-0">
            <span className="text-[9px] sm:text-[10px] font-bold uppercase px-2 py-0.5 bg-zinc-700 text-zinc-300 rounded">
              Q{game.quarter}
            </span>
            <span className="text-white text-lg sm:text-xl font-black tabular-nums leading-none">
              {game.time}
            </span>
            {game.play_clock && (
              <span className="text-yellow-500 text-[9px] sm:text-[10px] tabular-nums">
                :{game.play_clock}
              </span>
            )}
          </div>

          {/* Home team */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 flex-row-reverse">
            <PossessionDot active={possession === "H"} />
            <TeamIcon url={homeLogoUrl} abbr={homeAbbr} color={homeColor} />
            <div className="hidden sm:block min-w-0 text-right">
              <p className="text-white font-bold text-sm truncate">{homeName}</p>
            </div>
            <div className="sm:hidden min-w-0 text-right">
              <p className="text-white font-bold text-xs">{homeAbbr}</p>
            </div>
            <TimeoutSquares remaining={game.home_timeouts} teamColor={homeColor} />
            <span
              className={`text-white text-xl sm:text-2xl md:text-3xl font-black mr-auto tabular-nums ${homeFlash}`}
              style={{ "--sb-team-color": homeColor }}
            >
              {homeDisplay}
            </span>
          </div>
        </div>

        {/* Down & Distance strip */}
        {downText && (
          <div className="flex items-center justify-center gap-3 mt-2 pt-2 border-t border-white/10">
            <span className="text-orange-400 font-bold text-xs sm:text-sm">{downText}</span>
            {ballOnText && <span className="text-zinc-500 text-xs">{ballOnText}</span>}
          </div>
        )}
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════
   TEAM ICON (logo or fallback letter)
   ═══════════════════════════════════════ */
const TeamIcon = ({ url, abbr, color, size = 32 }) => {
  if (url) {
    return (
      <div
        className="rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden flex-shrink-0"
        style={{ width: size, height: size, borderColor: color, borderWidth: 2 }}
      >
        <img src={url} alt={abbr} className="w-full h-full object-contain p-0.5" />
      </div>
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, backgroundColor: color }}
    >
      <span className="text-white font-black text-[10px]">{abbr?.[0] || "?"}</span>
    </div>
  );
};


/* ═══════════════════════════════════════
   LIVE FIELD VIEW — ESPN-style horizontal strip
   ═══════════════════════════════════════ */
const LiveFieldView = ({ game, homeTeam, awayTeam }) => {
  const homeColor = homeTeam?.color || "#1a1a2e";
  const awayColor = awayTeam?.color || "#800000";
  const homeAbbr = homeTeam?.abbreviation || homeTeam?.name?.substring(0, 3)?.toUpperCase() || "HME";
  const awayAbbr = awayTeam?.abbreviation || awayTeam?.name?.substring(0, 3)?.toUpperCase() || "AWY";

  const ballOn = parseInt(game.ball_on) || 0;
  const toGo = parseInt(game.to_go) || 0;
  const down = parseInt(game.down) || 0;
  const possession = game.possession; // "H" = Home, "A" or "V" = Away/Visitor

  const isAway = possession === "A" || possession === "V";

  // Field is 100 yards. Away endzone on left (0), Home endzone on right (100).
  // Ball_On from ScoreBird is the yard line from the POV of the possessing team.
  let absPosition;
  if (isAway) {
    absPosition = ballOn; // Away drives toward home endzone (right)
  } else {
    absPosition = 100 - ballOn; // Home drives toward away endzone (left)
  }

  // First down marker
  let firstDownAbs;
  if (isAway) {
    firstDownAbs = absPosition + toGo;
  } else {
    firstDownAbs = absPosition - toGo;
  }

  // Clamp to field boundaries
  absPosition = Math.max(0, Math.min(100, absPosition));
  firstDownAbs = Math.max(0, Math.min(100, firstDownAbs || 0));

  // Convert to SVG X coordinates (field is drawn from x=60 to x=940 in a 1000-wide SVG)
  // Endzones: 0-60 (away) and 940-1000 (home)
  const EZ_W = 60;
  const FIELD_L = EZ_W;
  const FIELD_W = 1000 - EZ_W * 2;
  const toX = (yardPct) => FIELD_L + (yardPct / 100) * FIELD_W;

  const ballX = toX(absPosition);
  const firstDownX = toX(firstDownAbs);

  // Yard line positions (every 10 yards)
  const yardLines = [10, 20, 30, 40, 50, 60, 70, 80, 90];

  return (
    <div className="bg-zinc-900 border border-white/10 overflow-hidden" data-testid="scorebird-field">
      <svg viewBox="0 0 1000 120" className="w-full" preserveAspectRatio="xMidYMid meet">
        {/* Field background */}
        <rect x={FIELD_L} y="0" width={FIELD_W} height="120" fill="#2d5a27" />

        {/* Alternating darker/lighter 5-yard sections */}
        {[...Array(20)].map((_, i) => (
          i % 2 === 0 ? (
            <rect
              key={i}
              x={FIELD_L + (i / 20) * FIELD_W}
              y="0"
              width={FIELD_W / 20}
              height="120"
              fill="rgba(0,0,0,0.08)"
            />
          ) : null
        ))}

        {/* Away endzone (left) */}
        <rect x="0" y="0" width={EZ_W} height="120" fill={awayColor} />
        <text x="30" y="60" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="10" fontWeight="900" letterSpacing="1" transform="rotate(-90 30 60)">
          {awayAbbr}
        </text>

        {/* Home endzone (right) */}
        <rect x={1000 - EZ_W} y="0" width={EZ_W} height="120" fill={homeColor} />
        <text x={1000 - 30} y="60" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="10" fontWeight="900" letterSpacing="1" transform={`rotate(90 ${1000 - 30} 60)`}>
          {homeAbbr}
        </text>

        {/* Endzone borders */}
        <line x1={FIELD_L} y1="0" x2={FIELD_L} y2="120" stroke="white" strokeWidth="2" />
        <line x1={1000 - EZ_W} y1="0" x2={1000 - EZ_W} y2="120" stroke="white" strokeWidth="2" />

        {/* Yard lines */}
        {yardLines.map((yd) => {
          const x = toX(yd);
          const label = yd <= 50 ? yd : 100 - yd;
          return (
            <g key={yd}>
              <line x1={x} y1="0" x2={x} y2="120" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
              <text x={x} y="16" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="10" fontWeight="700">
                {label}
              </text>
              <text x={x} y="114" textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize="10" fontWeight="700">
                {label}
              </text>
            </g>
          );
        })}

        {/* Hash marks (every 1 yard) */}
        {[...Array(99)].map((_, i) => {
          const x = toX(i + 1);
          return (
            <g key={`hash-${i}`}>
              <line x1={x} y1="38" x2={x} y2="42" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
              <line x1={x} y1="78" x2={x} y2="82" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
            </g>
          );
        })}

        {/* First down line (yellow) */}
        {down > 0 && toGo > 0 && (
          <line
            x1={firstDownX} y1="0" x2={firstDownX} y2="120"
            stroke="#eab308" strokeWidth="2.5" strokeDasharray="4 2" opacity="0.9"
          />
        )}

        {/* Line of scrimmage (blue) */}
        {ballOn > 0 && (
          <line
            x1={ballX} y1="0" x2={ballX} y2="120"
            stroke="#3b82f6" strokeWidth="2.5" opacity="0.8"
          />
        )}

        {/* Ball marker (teardrop pin) */}
        {ballOn > 0 && (
          <g transform={`translate(${ballX}, 60)`}>
            {/* Pin shadow */}
            <ellipse cx="0" cy="8" rx="8" ry="3" fill="rgba(0,0,0,0.3)" />
            {/* Pin body */}
            <circle cx="0" cy="-8" r="10" fill={!isAway ? homeColor : awayColor} stroke="white" strokeWidth="1.5" />
            <text x="0" y="-4" textAnchor="middle" fill="white" fontSize="8" fontWeight="900">
              {!isAway ? homeAbbr : awayAbbr}
            </text>
            {/* Pin point */}
            <polygon points="-4,-1 4,-1 0,8" fill={!isAway ? homeColor : awayColor} />
          </g>
        )}

      </svg>
    </div>
  );
};


/* Ordinal suffix: 1→st, 2→nd, 3→rd, 4→th */
function ordSuffix(n) {
  const num = parseInt(n);
  if ([11, 12, 13].includes(num % 100)) return "th";
  const last = num % 10;
  if (last === 1) return "st";
  if (last === 2) return "nd";
  if (last === 3) return "rd";
  return "th";
}


/* ═══════════════════════════════════════
   POWERED-BY STRIP
   ═══════════════════════════════════════ */
const PoweredByScoreBird = () => (
  <a
    href="https://scorebird.com"
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center justify-center gap-1.5 py-1 mb-0.5 hover:opacity-80 transition-opacity"
    data-testid="scorebird-powered-strip"
  >
    <span className="text-zinc-500 text-[11px] tracking-wide" style={{ fontFamily: "'Legacy', sans-serif" }}>
      GameCast Powered By
    </span>
    <img src="/scorebird-logo.png" alt="ScoreBird" className="h-3.5 w-auto opacity-60" />
  </a>
);


/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
const ScoreBirdGameCast = ({ scorebirdLink, homeTeam, awayTeam }) => {
  const { data, error, loading } = useScoreBirdData(scorebirdLink);

  if (loading) {
    return (
      <div className="bg-zinc-900 border border-white/10 p-8 flex items-center justify-center" data-testid="scorebird-loading">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-zinc-700 border-t-yellow-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-zinc-500 text-xs uppercase tracking-wider">Connecting to ScoreBird...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-zinc-900 border border-white/10 p-6 text-center" data-testid="scorebird-error">
        <p className="text-zinc-500 text-sm">{error || "No scoreboard data available"}</p>
      </div>
    );
  }

  return (
    <div className="space-y-0" data-testid="scorebird-container">
      <PoweredByScoreBird />
      <ScoreBirdHeader game={data} homeTeam={homeTeam} awayTeam={awayTeam} />
      {data.sport === "football" && (
        <LiveFieldView game={data} homeTeam={homeTeam} awayTeam={awayTeam} />
      )}
    </div>
  );
};

export { ScoreBirdHeader, LiveFieldView, PoweredByScoreBird };
export default ScoreBirdGameCast;
